import { useEffect, useReducer, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { toast } from '../lib/toast'
import { useChannel, useUpdateChannel } from '../lib/queries'
import { displayUrl, twitterAuthor } from '../lib/displayUrl'
import type { ChatLink, LinkCategory, LinkEvent } from '../types'
import { ALL_CATEGORIES, CATEGORY_LABELS } from '../components/CategoryBadge'
import BrandIcon from '../components/BrandIcon'
import LinkCard from '../components/LinkCard'
import PreviewPanel from '../components/PreviewPanel'
import AppShell from '../components/AppShell'
import { SkeletonCard } from '../components/ui/Skeleton'
import { DoubleConfirmButton } from '../components/ui/DoubleConfirmButton'

interface State {
  unread: ChatLink[]
  read: ChatLink[]
  exitingIds: Set<string>
}

type Action =
  | { type: 'INIT'; unread: ChatLink[]; read: ChatLink[] }
  | { type: 'WS'; event: LinkEvent }
  | { type: 'EXIT'; id: string }
  | { type: 'COMMIT_READ'; link: ChatLink }
  | { type: 'REMOVE'; id: string }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'INIT':
      return { unread: action.unread, read: action.read, exitingIds: new Set() }

    case 'WS': {
      const e = action.event
      switch (e.type) {
        case 'link.new':
          // Already in unread? Replace; otherwise prepend
          if (state.unread.some((l) => l.id === e.link.id)) {
            return { ...state, unread: state.unread.map((l) => (l.id === e.link.id ? e.link : l)) }
          }
          return { ...state, unread: [e.link, ...state.unread] }
        case 'link.updated':
          return {
            ...state,
            unread: state.unread.map((l) => (l.id === e.link.id ? e.link : l)),
            read: state.read.map((l) => (l.id === e.link.id ? e.link : l)),
          }
        case 'link.read':
          return {
            ...state,
            unread: state.unread.filter((l) => l.id !== e.link.id),
            read: state.read.some((l) => l.id === e.link.id)
              ? state.read.map((l) => (l.id === e.link.id ? e.link : l))
              : [e.link, ...state.read],
          }
        case 'link.deleted':
          return {
            ...state,
            unread: state.unread.filter((l) => l.id !== e.id),
            read: state.read.filter((l) => l.id !== e.id),
          }
        case 'link.cleared':
          // Backend ClearAll → bütün liste sıfırlanır. Trigger'dan da DELETE event'leri
          // gelir ama bu event onları yutar (deduplicate doğal): listeler zaten boş.
          return { unread: [], read: [], exitingIds: new Set() }
      }
      return state
    }

    case 'EXIT': {
      const next = new Set<string>(state.exitingIds)
      next.add(action.id)
      return { ...state, exitingIds: next }
    }

    case 'COMMIT_READ': {
      const next = new Set<string>(state.exitingIds)
      next.delete(action.link.id)
      return {
        ...state,
        unread: state.unread.filter((l) => l.id !== action.link.id),
        read: state.read.some((l) => l.id === action.link.id)
          ? state.read.map((l) => (l.id === action.link.id ? action.link : l))
          : [action.link, ...state.read],
        exitingIds: next,
      }
    }

    case 'REMOVE':
      return {
        ...state,
        unread: state.unread.filter((l) => l.id !== action.id),
        read: state.read.filter((l) => l.id !== action.id),
      }
  }
}

const PREFS_KEY = 'linkPrefs.selected'

function loadFilter(channelId: string): Set<LinkCategory> {
  try {
    const raw = localStorage.getItem(`${PREFS_KEY}:${channelId}`)
    if (raw) {
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) {
        const valid = arr.filter((c): c is LinkCategory => ALL_CATEGORIES.includes(c))
        return new Set(valid)
      }
    }
  } catch {}
  // Varsayılan: tüm kategoriler seçili
  return new Set(ALL_CATEGORIES)
}

function saveFilter(channelId: string, set: Set<LinkCategory>) {
  localStorage.setItem(`${PREFS_KEY}:${channelId}`, JSON.stringify(Array.from(set)))
}

function FilterPill({
  active,
  onClick,
  category,
  children,
}: {
  active: boolean
  onClick: () => void
  category?: LinkCategory // varsa aktif state'te kategorinin brand rengini kullan
  children: React.ReactNode
}) {
  const baseInactive =
    'bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-border-strong)] hover:text-[var(--color-text)]'

  // Aktif + kategori varsa: brand color + soft tint (CategoryBadge ile aynı pattern)
  if (active && category) {
    const c = `var(--color-cat-${category})`
    return (
      <button
        onClick={onClick}
        style={{
          color: c,
          borderColor: c + '88',
          background: c + '22',
        }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap"
      >
        {children}
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap',
        active
          ? 'bg-[var(--color-accent-soft)] border-[var(--color-accent)]/40 text-white'
          : baseInactive,
      ].join(' ')}
    >
      {children}
    </button>
  )
}

export default function LinksPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: channel, isLoading: chLoading } = useChannel(id)
  const updateChannel = useUpdateChannel(id || '')
  const [selected, setSelected] = useState<ChatLink | null>(null)
  const [state, dispatch] = useReducer(reducer, { unread: [], read: [], exitingIds: new Set<string>() })
  const [filter, setFilter] = useState<Set<LinkCategory>>(() => loadFilter(id || ''))
  const [previewCollapsed, setPreviewCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('linkPrefs.previewCollapsed') === '1'
    } catch {
      return false
    }
  })

  // İlk yükleme — WS hybrid pattern: links endpoint'i useReducer ile yönetilir,
  // kanal bilgisi useQuery cache'e koyulur (Settings'le paylaşılır).
  useEffect(() => {
    if (!id) return
    Promise.all([
      api<ChatLink[]>(`/api/channels/${id}/links?status=unread&limit=100`),
      api<ChatLink[]>(`/api/channels/${id}/links?status=read&limit=100`),
    ])
      .then(([unread, read]) => {
        dispatch({ type: 'INIT', unread, read })
      })
      .catch(() => navigate('/dashboard'))
  }, [id])

  // WS subscribe
  useEffect(() => {
    if (!id) return
    const token = localStorage.getItem('auth.token')
    if (!token) return
    const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8080'
    const wsBase = apiBase.replace(/^http/, 'ws')
    let ws: WebSocket | null = null
    let backoff = 1000
    let cancelled = false
    function connect() {
      if (cancelled) return
      ws = new WebSocket(`${wsBase}/ws/links/${id}?token=${encodeURIComponent(token!)}`)
      ws.onopen = () => { backoff = 1000 }
      ws.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data) as LinkEvent
          dispatch({ type: 'WS', event })
        } catch {}
      }
      ws.onclose = () => {
        if (cancelled) return
        setTimeout(connect, backoff)
        backoff = Math.min(backoff * 2, 30000)
      }
    }
    connect()
    return () => { cancelled = true; ws?.close() }
  }, [id])

  // Filter persistence
  useEffect(() => {
    if (id) saveFilter(id, filter)
  }, [id, filter])

  // Preview collapse persistence
  useEffect(() => {
    try {
      localStorage.setItem('linkPrefs.previewCollapsed', previewCollapsed ? '1' : '0')
    } catch {
      /* sessionStorage disabled — yut */
    }
  }, [previewCollapsed])

  async function consume(link: ChatLink, mode: 'open' | 'preview' | 'mark') {
    if (!id) return
    if (mode === 'open') {
      window.open(displayUrl(link), '_blank', 'noopener,noreferrer')
    } else if (mode === 'preview') {
      setSelected(link)
      // Önizleme tıklandığında panel kapalıysa otomatik aç.
      if (previewCollapsed) setPreviewCollapsed(false)
    }
    // mode === 'mark' yalnızca okundu işaretler — açma veya preview yapmaz
    if (link.read_at) return // zaten okunmuş, sadece aksiyonu yap
    dispatch({ type: 'EXIT', id: link.id })
    setTimeout(async () => {
      try {
        const updated = await api<ChatLink>(`/api/channels/${id}/links/${link.id}/read`, { method: 'POST' })
        dispatch({ type: 'COMMIT_READ', link: updated })
      } catch {
        dispatch({ type: 'COMMIT_READ', link })
      }
    }, 300)
  }

  function toggleTracking() {
    if (!channel) return
    const wasAuto = channel.link_tracking_auto_managed
    const nextEnabled = !channel.link_tracking_enabled
    // Manuel toggle her zaman auto modu kapatır — kullanıcı manuel kontrole geçmiş demektir.
    // Toast ile uyarı: yalnızca auto'dan çıkıyorsa göster.
    updateChannel.mutate(
      {
        link_tracking_enabled: nextEnabled,
        link_tracking_auto_managed: false,
      },
      {
        onSuccess: () => {
          if (wasAuto) {
            toast('Otomatik mod kapatıldı', {
              description: 'Yayın açıldığında artık otomatik başlamayacak. Ayarlar > Bot’tan tekrar açabilirsin.',
            })
          }
        },
        onError: (e) => toast.error((e as Error).message),
      },
    )
  }

  function startTrackingFromCTA() {
    if (!channel) return
    updateChannel.mutate(
      {
        link_tracking_enabled: true,
        // CTA'dan başlatma: auto modu da koru/aç (varsa). Burada false set etmiyoruz —
        // mevcut auto durumu korunur (Settings'ten ayarlanır).
      },
      { onError: (e) => toast.error((e as Error).message) },
    )
  }

  function toggleCategory(c: LinkCategory) {
    setFilter((prev) => {
      const next = new Set(prev)
      if (next.has(c)) next.delete(c)
      else next.add(c)
      return next
    })
  }

  function selectAll() {
    setFilter(new Set(ALL_CATEGORIES))
  }

  if (chLoading || !channel || !id) {
    return (
      <AppShell activeChannelId={id || null}>
        <div className="p-6 space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </AppShell>
    )
  }

  const allSelected = filter.size === ALL_CATEGORIES.length
  const filteredUnread = state.unread.filter((l) => filter.has(l.category))
  const filteredRead = state.read.filter((l) => filter.has(l.category))

  const highlightSet =
    channel.highlight_authors_enabled && channel.highlighted_twitter_authors.length > 0
      ? new Set(channel.highlighted_twitter_authors.map((a) => a.toLowerCase()))
      : null
  function isHighlighted(l: ChatLink): boolean {
    if (!highlightSet) return false
    const a = twitterAuthor(l)
    return !!a && highlightSet.has(a.toLowerCase())
  }

  return (
    <AppShell activeChannelId={id || null}>
      <div className="h-full flex flex-col px-6 py-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <button onClick={() => navigate(`/channels/${channel.id}`)} className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-2">
              ← {channel.slug}
            </button>
            <h1 className="text-xl font-semibold">Linkler</h1>
          </div>
          {channel.is_owner && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] cursor-pointer">
                <input type="checkbox" checked={channel.link_tracking_enabled} onChange={toggleTracking} className="accent-[var(--color-accent)]" />
                Takibi aktifleştir
              </label>
              <label
                className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] cursor-pointer ${
                  !channel.link_tracking_enabled || !channel.bot_reply || channel.oauth_status !== 'ok'
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
                title={
                  !channel.link_tracking_enabled
                    ? 'Önce link takibini aç'
                    : !channel.bot_reply || channel.oauth_status !== 'ok'
                      ? 'Bot reply açık olmalı (Ayarlar > Bot)'
                      : 'Daha önce okunmuş linkler tekrar paylaşılırsa chat\'e cevap ver'
                }
              >
                <input
                  type="checkbox"
                  checked={channel.link_dup_reply_enabled}
                  onChange={() =>
                    updateChannel.mutate(
                      { link_dup_reply_enabled: !channel.link_dup_reply_enabled },
                      { onError: (e) => toast.error((e as Error).message) },
                    )
                  }
                  disabled={!channel.link_tracking_enabled || !channel.bot_reply || channel.oauth_status !== 'ok'}
                  className="accent-[var(--color-accent)]"
                />
                Tekrar atılana cevap ver
              </label>
            </div>
          )}
        </div>

        {/* Tracking off banner — kullanıcıyı durumdan haberdar et + tek-tık aç. */}
        {channel.is_owner && !channel.link_tracking_enabled && (
          <div className="mb-4 px-4 py-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-base">{channel.link_tracking_auto_managed ? '🟢' : '⏸'}</span>
              <div className="text-sm min-w-0">
                {channel.link_tracking_auto_managed ? (
                  <>
                    <div className="font-medium">Otomatik mod aktif</div>
                    <div className="text-xs text-[var(--color-text-muted)] truncate">
                      Yayın açıldığında link takibi otomatik başlayacak. Şimdi de elle başlatabilirsin.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">Link takibi kapalı</div>
                    <div className="text-xs text-[var(--color-text-muted)] truncate">
                      Chat'teki linkleri toplamak için takibi başlat. Otomatik moda almak için Ayarlar &gt; Bot.
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={startTrackingFromCTA}
              className="shrink-0 px-3 py-1.5 rounded-md bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white text-xs font-medium flex items-center gap-1.5"
            >
              <span>▶</span>
              Takibi başlat
            </button>
          </div>
        )}

        {/* Pill filter bar — kategoriler */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <FilterPill active={allSelected} onClick={selectAll}>
            <span className="text-[var(--color-text-subtle)]">⊕</span>
            Hepsi
          </FilterPill>
          {ALL_CATEGORIES.map((c) => (
            <FilterPill key={c} active={filter.has(c)} category={c} onClick={() => toggleCategory(c)}>
              <BrandIcon category={c} size={11} />
              {CATEGORY_LABELS[c]}
            </FilterPill>
          ))}
        </div>

        <div
          className={`grid grid-cols-1 ${previewCollapsed ? 'md:grid-cols-[1fr_44px]' : 'md:grid-cols-[1fr_400px]'} gap-4 flex-1 min-h-0 transition-[grid-template-columns] duration-200`}
        >
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden flex flex-col">
            {/* Unread */}
            <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Yeni · {filteredUnread.length}
                {filter.size < ALL_CATEGORIES.length && (
                  <span className="ml-1.5 text-[var(--color-text-subtle)] normal-case font-normal">
                    ({state.unread.length} toplam)
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {filteredUnread.length > 0 && (
                  <DoubleConfirmButton
                    idle={
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Tümünü okundu işaretle
                      </>
                    }
                    armed={
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 9v4M12 17h.01" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        Emin misin? Tekrar tıkla
                      </>
                    }
                    onConfirm={async () => {
                      try {
                        await api(`/api/channels/${id}/links/mark-all-read`, { method: 'POST' })
                        toast.success('Tüm linkler okundu işaretlendi')
                      } catch (e) {
                        toast.error((e as Error).message)
                      }
                    }}
                  />
                )}
                {channel.is_owner && (state.unread.length > 0 || state.read.length > 0) && (
                  <DoubleConfirmButton
                    idle={
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        </svg>
                        Tümünü temizle
                      </>
                    }
                    armed={
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 9v4M12 17h.01" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        Geri alınamaz! Tekrar tıkla
                      </>
                    }
                    onConfirm={async () => {
                      try {
                        await api(`/api/channels/${id}/links/clear-all`, { method: 'POST' })
                        toast.success('Tüm linkler temizlendi')
                      } catch (e) {
                        toast.error((e as Error).message)
                      }
                    }}
                  />
                )}
              </div>
            </div>
            <div className="overflow-y-auto p-2 space-y-1 max-h-[40vh]">
              {filteredUnread.length === 0 && (
                <div className="text-xs text-[var(--color-text-subtle)] px-2 py-4 text-center">
                  {state.unread.length === 0 ? 'Yeni link yok.' : 'Filtreye uyan yeni link yok.'}
                </div>
              )}
              {filteredUnread.map((l) => (
                <LinkCard
                  key={l.id}
                  link={l}
                  selected={selected?.id === l.id}
                  onOpen={() => consume(l, 'open')}
                  onPreview={() => consume(l, 'preview')}
                  onMarkRead={() => consume(l, 'mark')}
                  exiting={state.exitingIds.has(l.id)}
                  highlight={isHighlighted(l)}
                />
              ))}
            </div>

            {/* Read */}
            <div className="px-4 py-3 border-y border-[var(--color-border)] bg-[var(--color-elevated)]/40">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Okunmuş · {filteredRead.length}
                {filter.size < ALL_CATEGORIES.length && (
                  <span className="ml-1.5 text-[var(--color-text-subtle)] normal-case font-normal">
                    ({state.read.length} toplam)
                  </span>
                )}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredRead.length === 0 && (
                <div className="text-xs text-[var(--color-text-subtle)] px-2 py-4 text-center">
                  Filtreye uyan okunmuş link yok.
                </div>
              )}
              {filteredRead.map((l) => (
                <LinkCard
                  key={l.id}
                  link={l}
                  selected={selected?.id === l.id}
                  onOpen={() => window.open(displayUrl(l), '_blank', 'noopener,noreferrer')}
                  onPreview={() => {
                    setSelected(l)
                    if (previewCollapsed) setPreviewCollapsed(false)
                  }}
                  highlight={isHighlighted(l)}
                />
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-2 py-2 border-b border-[var(--color-border)] shrink-0">
              {!previewCollapsed && (
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)] px-2">
                  Önizleme
                </h3>
              )}
              <button
                onClick={() => setPreviewCollapsed((v) => !v)}
                title={previewCollapsed ? 'Önizlemeyi aç' : 'Önizlemeyi gizle'}
                className="ml-auto p-1.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {previewCollapsed ? (
                    <polyline points="15 18 9 12 15 6" />
                  ) : (
                    <polyline points="9 18 15 12 9 6" />
                  )}
                </svg>
              </button>
            </div>
            {!previewCollapsed && <PreviewPanel channelId={id} link={selected} />}
          </div>
        </div>
      </div>

    </AppShell>
  )
}
